import React, { useEffect, useMemo, useState } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import {
  POStatCard,
  POFilters,
  POTable,
  LowStockSuggestions,
  CreatePurchaseOrderModal,
} from './components';
import {
  POStatus,
  PurchaseOrder,
  POStatData,
  LowStockItem,
  backendStatusToUi,
} from './purchaseOrdersTypes';
import { usePurchaseOrdersStore } from '../../../store/purchaseOrdersStore';
import { useSkusStore } from '../../../store/skusStore';
import { PurchaseOrder as BackendPO, autoGeneratePurchaseOrder } from '../../../services/purchaseOrdersApi';
import styles from './AdminPurchaseOrders.module.css';

type TabKey = 'all' | POStatus;

function progressOf(po: BackendPO): number {
  if (po.status === 'received') return 100;
  if (po.status === 'cancelled') return 0;
  if (po.status === 'partial' && po.items?.length) {
    const totalOrdered = po.items.reduce((s, i) => s + i.quantity_ordered, 0);
    const totalReceived = po.items.reduce((s, i) => s + i.quantity_received, 0);
    return totalOrdered > 0 ? Math.round((totalReceived / totalOrdered) * 100) : 0;
  }
  if (po.status === 'sent') return 50;
  return 0;
}

function adapt(po: BackendPO): PurchaseOrder {
  return {
    id: String(po.id),
    poNumber: po.po_number,
    vendor: po.supplier_name || '—',
    warehouse: '—',
    created: new Date(po.order_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    expected: po.expected_date ? new Date(po.expected_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—',
    items: po.items?.length ?? 0,
    total: po.total_amount,
    progress: progressOf(po),
    status: backendStatusToUi(po.status),
    backendStatus: po.status,
  };
}

const AdminPurchaseOrders: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [actionMsg, setActionMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const items = usePurchaseOrdersStore((s) => s.items);
  const loading = usePurchaseOrdersStore((s) => s.loading);
  const error = usePurchaseOrdersStore((s) => s.error);
  const fetchList = usePurchaseOrdersStore((s) => s.fetchList);
  const approvePO = usePurchaseOrdersStore((s) => s.approve);
  const setPOStatus = usePurchaseOrdersStore((s) => s.setStatus);

  const skus = useSkusStore((s) => s.items);
  const fetchSkus = useSkusStore((s) => s.fetchList);

  const [autoGenBusy, setAutoGenBusy] = useState(false);
  const [autoGenMsg, setAutoGenMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleAutoGenerate = async () => {
    setAutoGenBusy(true);
    setAutoGenMsg(null);
    try {
      const po = await autoGeneratePurchaseOrder(25);
      setAutoGenMsg({ tone: 'ok', text: `Created draft ${po.po_number} with ${po.items?.length ?? 0} item(s)` });
      await fetchList();
    } catch (err: any) {
      const msg = err?.message || 'Failed to auto-generate PO';
      setAutoGenMsg({ tone: 'err', text: msg.includes('NO_LOW_STOCK') ? 'No SKUs are below the low-stock threshold' : msg });
    } finally {
      setAutoGenBusy(false);
      setTimeout(() => setAutoGenMsg(null), 5000);
    }
  };

  useEffect(() => {
    fetchList();
    fetchSkus();
  }, [fetchList, fetchSkus]);

  const adapted = useMemo<PurchaseOrder[]>(() => items.map(adapt), [items]);

  const tabCounts = useMemo<Record<string, number>>(() => {
    const counts: Record<string, number> = { all: adapted.length };
    for (const status of ['draft', 'pending_approval', 'approved', 'sent', 'transit', 'partial', 'delayed', 'done']) {
      counts[status] = adapted.filter((o) => o.status === status).length;
    }
    return counts;
  }, [adapted]);

  // VP-ADM-003 — a PO must be Admin-approved before it can be sent. Draft/
  // pending_approval POs get Approve+Reject; an approved PO gets Send.
  const runAction = async (order: PurchaseOrder, action: 'approve' | 'reject' | 'send') => {
    setBusyId(order.id);
    setActionMsg(null);
    try {
      if (action === 'approve') {
        await approvePO(order.id);
        setActionMsg({ tone: 'ok', text: `${order.poNumber} approved — it can now be sent to the supplier.` });
      } else if (action === 'reject') {
        await setPOStatus(order.id, 'cancelled');
        setActionMsg({ tone: 'ok', text: `${order.poNumber} rejected and cancelled.` });
      } else {
        await setPOStatus(order.id, 'sent');
        setActionMsg({ tone: 'ok', text: `${order.poNumber} sent to ${order.vendor}.` });
      }
    } catch (err: any) {
      setActionMsg({ tone: 'err', text: err?.message || `Failed to ${action} ${order.poNumber}` });
    } finally {
      setBusyId(null);
      setTimeout(() => setActionMsg(null), 5000);
    }
  };

  const filtered = useMemo(() => {
    return adapted.filter((order) => {
      const matchesSearch =
        searchQuery === '' ||
        order.poNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.vendor.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesTab = activeTab === 'all' || order.status === activeTab;
      return matchesSearch && matchesTab;
    });
  }, [adapted, searchQuery, activeTab]);

  const lowStock = useMemo<LowStockItem[]>(
    () =>
      skus
        .filter((s) => s.total_stock < 50)
        .slice(0, 8)
        .map((s) => ({
          id: String(s.id),
          name: s.product_name,
          sku: s.sku_code,
          currentStock: s.total_stock,
          reorderPoint: 50,
          suggestedQty: Math.max(50, 100 - s.total_stock),
        })),
    [skus]
  );

  const stats: POStatData[] = [
    { id: 'needs-approval', title: 'Needs Approval', subtitle: 'Draft — awaiting Admin sign-off', value: String((tabCounts.draft || 0) + (tabCounts.pending_approval || 0)).padStart(2, '0'), color: 'blue' },
    { id: 'approved', title: 'Approved', subtitle: 'Ready to send', value: String(tabCounts.approved).padStart(2, '0'), color: 'yellow' },
    { id: 'arriving', title: 'Sent', subtitle: 'Awaiting delivery', value: String(tabCounts.sent).padStart(2, '0'), color: 'orange' },
    { id: 'partial', title: 'Partially Received', subtitle: 'Awaiting balance', value: String(tabCounts.partial).padStart(2, '0'), color: 'red' },
    { id: 'completed', title: 'Completed', subtitle: 'Stock updated & closed', value: String(tabCounts.done).padStart(2, '0'), color: 'green' },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Purchase orders</h1>
          <p className={styles.subtitle}>Manage incoming stock operations — what needs to happen today</p>
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.autoGenBtn}
            onClick={handleAutoGenerate}
            disabled={autoGenBusy}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" strokeLinecap="round" />
            </svg>
            {autoGenBusy ? 'Generating…' : 'Auto Generate from Low stock'}
          </button>
          <button
            type="button"
            className={styles.newBtn}
            onClick={() => setShowCreateModal(true)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" strokeLinecap="round" />
            </svg>
            New Purchase Order
          </button>
        </div>
      </div>

      {autoGenMsg && (
        <p style={{ color: autoGenMsg.tone === 'ok' ? '#059669' : '#DC2626', fontSize: 13, margin: '4px 0 12px' }}>
          {autoGenMsg.text}
        </p>
      )}
      {actionMsg && (
        <p style={{ color: actionMsg.tone === 'ok' ? '#059669' : '#DC2626', fontSize: 13, margin: '4px 0 12px' }}>
          {actionMsg.text}
        </p>
      )}

      <div className={styles.statsGrid}>
        {stats.map((stat) => (
          <POStatCard key={stat.id} stat={stat} />
        ))}
      </div>

      <POFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabCounts={tabCounts}
      />

      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && filtered.length === 0}
        emptyMessage="No purchase orders yet"
        onRetry={() => fetchList()}
      >
        <POTable
          orders={filtered}
          busyId={busyId}
          onApprove={(order) => runAction(order, 'approve')}
          onReject={(order) => runAction(order, 'reject')}
          onSend={(order) => runAction(order, 'send')}
        />
      </AsyncBoundary>

      <LowStockSuggestions items={lowStock} onCreatePO={() => setShowCreateModal(true)} />

      <CreatePurchaseOrderModal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          fetchList().catch(() => undefined);
        }}
      />
    </div>
  );
};

export const AdminPurchaseOrdersPage: React.FC = () => (
  <ProtectedRoute requiredRoles={['admin', 'superadmin']}>
    <AdminPurchaseOrders />
  </ProtectedRoute>
);

export default AdminPurchaseOrders;
